/**
 * Cross-tab session claims.
 *
 * ``localStorage`` is shared across every browser tab on the origin, so the
 * recovery sweep sees session entries written by tabs that are still running.
 * Finalizing one of those as ``ABANDONED`` both mints a false loss-signal and
 * deletes that session's crash-recovery record out from under it — so before
 * sweeping, we ask.
 *
 * Every tab holding active trackers answers a ``ping`` with the session ids it
 * owns. Whatever comes back is live and off-limits; anything left in storage is
 * a genuine orphan.
 *
 * Why ask rather than track liveness: a heartbeat has to infer "still open"
 * from a timestamp, which means picking a staleness window and hoping
 * background-timer throttling stays inside it. Asking is exact — a context that
 * replies is running, by definition — and needs no window, no clock comparison
 * and no stored state of its own.
 *
 * Limits, both inherent rather than specific to this design: a frozen or
 * discarded background tab cannot answer and so reads as gone (its entry is
 * recovered with the duration it had banked, and its session — which is paused
 * and accruing nothing while hidden — re-persists under the same ``sessionId``
 * when the user returns, so the read side still lands on the right total). And
 * where ``BroadcastChannel`` is unavailable there are no peers to consult, so
 * the sweep finalizes every entry this tab doesn't own — the pre-fix behaviour
 * for other tabs' sessions. This tab's own live sessions stay protected either
 * way, since the sweep accounts for them without the channel.
 */

const CHANNEL_NAME = "timeTracker:sessionClaims";

// How long to wait for peers to answer. Peers reply from a message handler, so
// this is bounded by task-queue latency rather than by any real work; 150ms is
// far more than a responsive tab needs and is only paid once per sweep, off the
// rendering path (recovery runs from an effect, the handoff is fire-and-forget).
export const CLAIM_WINDOW_MS = 150;

interface PingMessage {
  type: "ping";
  nonce: string;
}

interface PongMessage {
  type: "pong";
  nonce: string;
  sessionIds: string[];
}

function isPing(data: unknown): data is PingMessage {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as PingMessage).type === "ping" &&
    typeof (data as PingMessage).nonce === "string"
  );
}

function isPong(data: unknown, nonce: string): data is PongMessage {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as PongMessage).type === "pong" &&
    (data as PongMessage).nonce === nonce &&
    Array.isArray((data as PongMessage).sessionIds)
  );
}

let responder: BroadcastChannel | null = null;

/**
 * Start answering claim pings, reporting whatever ``getLiveSessionIds`` returns
 * at the moment each ping arrives. Idempotent — the tracker registry calls this
 * on every registration and only the first one opens the channel.
 *
 * Reading the ids per ping rather than at subscribe time is what lets this be a
 * plain listener instead of a timer: there is no cached state to keep fresh, so
 * a paused tracker (which stops its periodic save) is claimed just as reliably
 * as an accruing one.
 */
export function startClaimResponder(
  getLiveSessionIds: () => Set<string>,
): void {
  if (responder !== null || typeof BroadcastChannel === "undefined") return;
  responder = new BroadcastChannel(CHANNEL_NAME);
  responder.onmessage = (event: MessageEvent) => {
    if (!isPing(event.data)) return;
    const sessionIds = [...getLiveSessionIds()];
    if (sessionIds.length === 0) return;
    responder?.postMessage({
      type: "pong",
      nonce: event.data.nonce,
      sessionIds,
    } satisfies PongMessage);
  };
}

/** Stop answering — called when this tab's last active tracker goes away. */
export function stopClaimResponder(): void {
  responder?.close();
  responder = null;
}

/**
 * Session ids claimed by whichever tabs answer the ping.
 *
 * A channel skips only the sending *object*, so this tab's own responder may
 * answer too. Callers must still account for their own live sessions directly
 * rather than rely on that — the sweep excludes them before asking: a tab's
 * first tracker opens its responder on registration, after the
 * ``startTracking`` handoff has already pinged, and a channel opened after
 * ``postMessage`` doesn't receive it. Without ``BroadcastChannel`` there is no
 * responder at all.
 */
export async function collectClaimedSessionIds(): Promise<Set<string>> {
  const claimed = new Set<string>();
  if (typeof BroadcastChannel === "undefined") return claimed;

  const nonce = crypto.randomUUID();
  const asker = new BroadcastChannel(CHANNEL_NAME);
  asker.onmessage = (event: MessageEvent) => {
    if (!isPong(event.data, nonce)) return;
    for (const id of event.data.sessionIds) {
      if (typeof id === "string") claimed.add(id);
    }
  };

  asker.postMessage({ type: "ping", nonce } satisfies PingMessage);
  await new Promise((resolve) => setTimeout(resolve, CLAIM_WINDOW_MS));
  asker.close();
  return claimed;
}

/** Test seam — drop the responder between cases. */
export function resetSessionClaimsForTests(): void {
  stopClaimResponder();
}
